import clsx from 'clsx';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { bounceIn, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { UseAskAttachmentUploadResult } from '../upload';
import { useAsk } from '../useAsk';
import { ASK_MAX_ATTACHMENTS } from '../util/askAttachmentTypes';
import { ASK_VOICE_INPUT_MODE, useAskDictation } from '../voice';
import { AskAttachmentTray } from './AskAttachmentTray';
import { AskCameraButton } from './AskCameraButton';
import { AskDictateButton } from './AskDictateButton';
import { AskDictationBar } from './AskDictationBar';
import { AskRecordButton } from './AskRecordButton';
import styles from './AskComposer.module.css';

/**
 * The three control glyphs, larger than a ghost button's default: they sit alongside a 5Q input pill
 * and are the only way to reach any of the three features.
 *
 * Deliberately not the same number. The attach glyph is heavier and fills its box, while the
 * microphone is a thinner outline with air around it, so matching them by pixel size makes the
 * attach button read as the bigger of the two. The camera is the widest of the three — a body
 * spanning most of its viewBox where the other two are tall and narrow — so it is set smallest of
 * all. The sizes differ so they look like they do not.
 */
const ATTACH_ICON_SIZE = grid(3.25);
const CAMERA_ICON_SIZE = grid(3.125);
const MICROPHONE_ICON_SIZE = grid(3.5);

/** How close to the limit the count appears, so it warns rather than nags. */
const COUNT_VISIBLE_WITHIN = 100;
/** Typing pause before the count is announced. A polite live region updated on every
    keystroke queues one announcement per character, which drowns out the input itself. */
const ANNOUNCE_DEBOUNCE_MS = 1000;

interface AskComposerProps {
  /**
   * Shared with the panel's drop target rather than obtained here, so a file refused by a drop
   * and one refused by the picker report through the same `rejection`.
   */
  upload: UseAskAttachmentUploadResult;
  /** Whether the attach and microphone controls are offered at all. */
  isFileAndVoiceEnabled: boolean;
  /**
   * Whether the agent may create cards. Only affects the words put in place of an empty question:
   * asking it to create cards when it cannot would be a request the user never made and the agent
   * cannot honour.
   */
  isCardCreateEnabled: boolean;
}

export function AskComposer({
  upload,
  isFileAndVoiceEnabled,
  isCardCreateEnabled,
}: AskComposerProps) {
  const {
    send,
    isStreaming,
    hasAsked,
    promptLimit,
    composerDraft,
    retainComposerDraft,
    attachments,
    removeAttachment,
  } = useAsk();
  const { pickAttachments, addDroppedFiles, addRecording, retryAttachment, rejection } = upload;
  const [value, setValue] = useState(composerDraft?.text ?? '');
  const inputRef = useRef<MBAutosizeTextInput>(null);
  const hintId = useId();

  // Focus the input when the panel opens. Deferred a frame so it runs after the
  // dialog is shown (the `autoFocus` attribute is unreliable inside dialog.show()).
  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  // A question handed to Ask from elsewhere — `/ask` in chat, or a chat draft — while the panel
  // was already open. Keyed on the draft's id rather than its text so it applies once per
  // question: without that, editing what was seeded would be undone on the next render.
  const draftId = composerDraft?.id;
  const draftText = composerDraft?.text;
  useEffect(() => {
    if (isNil(draftId)) {
      return;
    }
    setValue(draftText ?? '');
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [draftId, draftText]);

  // Hand the text back on the way out, so closing the panel with an unsent question does not
  // lose it — whatever it came from has already let go of it. Reads from a ref because an
  // unmount cleanup closes over the value from the render it was created in.
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => () => retainComposerDraft(valueRef.current), [retainComposerDraft]);

  /**
   * What the input held when dictation began.
   *
   * Speech is appended to it rather than replacing it, so starting to dictate never destroys a
   * half-typed question — and discarding a dictation restores exactly this, which is why it is
   * remembered rather than recovered by subtracting the transcript.
   */
  const dictationBaseRef = useRef('');
  /**
   * What dictation put into this draft, kept so the wire prompt can say which words were heard
   * rather than typed. Cleared on discard and after sending; deliberately *not* cleared when the
   * user edits, because an edit fixes the words they noticed and not the ones they did not.
   */
  const dictatedTextRef = useRef<string | undefined>(undefined);
  const handleDictationStart = useCallback(() => {
    dictationBaseRef.current = valueRef.current;
  }, []);
  const handleDictationChange = useCallback((text: string) => {
    const base = dictationBaseRef.current;
    dictatedTextRef.current = text;
    setValue(isEmpty(base) ? text : `${base} ${text}`);
  }, []);
  const handleDictationEnd = useCallback((outcome: 'commit' | 'cancel') => {
    if (outcome === 'cancel') {
      setValue(dictationBaseRef.current);
      dictatedTextRef.current = undefined;
    }
    dictationBaseRef.current = '';
  }, []);

  /**
   * Held here rather than inside either piece of UI that uses it: the microphone sits in the row of
   * controls, while the bar that replaces it during dictation sits above the input, out of that row
   * entirely. Two places in the DOM, so one owner above both.
   */
  const dictation = useAskDictation({
    onStart: handleDictationStart,
    onText: handleDictationChange,
    onEnd: handleDictationEnd,
  });

  const placeholder = hasAsked ? i18n('ask.placeholderSubsequent') : i18n('ask.placeholderInitial');
  // Counted on the trimmed value because that is what `send` puts on the wire.
  const length = value.trim().length;
  const isOverLimit = length > promptLimit;
  const isAnyUploading = attachments.some(attachment => attachment.status === 'uploading');
  const hasReadyAttachment = attachments.some(attachment => attachment.status === 'ready');
  // A question can be just files: the prompt below stands in for the words.
  const canSend = !isEmpty(value.trim()) || hasReadyAttachment;
  const isTrayFull = attachments.length >= ASK_MAX_ATTACHMENTS;
  // Typing past the limit stays allowed; it just cannot be sent, which is easier to act on
  // than an input that stops accepting text mid-paste.
  const showCount = promptLimit - length <= COUNT_VISIBLE_WITHIN;

  // The live region below is mounted for the life of the composer: a region inserted into the
  // DOM together with its text is routinely dropped, which is exactly the moment — the send
  // button going disabled — that the count exists to explain.
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    const message = (() => {
      if (isOverLimit) {
        return i18n('ask.charCountOver', { limit: promptLimit });
      }
      if (showCount) {
        return i18n('ask.charCountRemaining', { remaining: promptLimit - length });
      }
      return '';
    })();
    const id = setTimeout(() => setAnnouncement(message), ANNOUNCE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [isOverLimit, showCount, length, promptLimit]);

  const { isActive: isDictating, finish: finishDictation } = dictation;
  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    // The gateway validates the prompt at one character or more, so files with no words get an
    // explicit sentence rather than an empty prompt and a rejected request. Which sentence depends
    // on what the agent is allowed to do: asking it to create cards where it cannot is a request
    // the user never made and the agent cannot honour.
    const emptyPrompt = isCardCreateEnabled
      ? i18n('ask.attachments.defaultPrompt')
      : i18n('ask.attachments.defaultPromptDescribe');
    const prompt = isEmpty(trimmed) ? emptyPrompt : trimmed;
    if ((isEmpty(trimmed) && !hasReadyAttachment) || isStreaming || isAnyUploading) {
      return;
    }
    if (trimmed.length > promptLimit) {
      return;
    }
    // Sending is the end of the dictation. The words are already in `value` — they arrive there as
    // they are spoken — so this changes nothing about what is sent; it closes the microphone, which
    // would otherwise stay open and keep typing into an input the user has just emptied.
    if (isDictating) {
      finishDictation();
    }
    send(prompt, { dictatedText: dictatedTextRef.current });
    setValue('');
    dictatedTextRef.current = undefined;
  }, [
    value,
    hasReadyAttachment,
    isStreaming,
    isAnyUploading,
    promptLimit,
    send,
    isDictating,
    finishDictation,
    isCardCreateEnabled,
  ]);

  const handlePick = useCallback(() => void pickAttachments(), [pickAttachments]);

  // A photo takes the path a dropped file takes, rather than one of its own. `addDroppedFiles` is
  // the general staging entry point — the name is where it was first needed, not the limit of what
  // it accepts — so the content-type allowlist, the 25 MB cap, the free-plan rule and the four-file
  // ceiling are applied to a photo by exactly the code that applies them to everything else.
  const handlePhoto = useCallback((file: File) => void addDroppedFiles([file]), [addDroppedFiles]);

  return (
    <div className={styles.composer}>
      {isFileAndVoiceEnabled && (
        <AskAttachmentTray
          attachments={attachments}
          onRemove={removeAttachment}
          onRetry={retryAttachment}
        />
      )}
      {!isEmpty(rejection) && (
        <span className={styles.rejection} role="alert" data-testid="ask-attachment-rejection">
          {rejection}
        </span>
      )}
      {/* Above the input rather than beside it: in the row of controls, the meter and its two
          buttons competed with the input for width and squeezed it to nothing. */}
      {isFileAndVoiceEnabled && <AskDictationBar dictation={dictation} />}
      <div className={clsx(styles.inputRow, isFileAndVoiceEnabled && styles.withControls)}>
        {isFileAndVoiceEnabled && (
          <div className={styles.attachControls}>
            <AnchoredTooltip tooltip={i18n('ask.attachments.add')} position="top">
              <Button
                variant="ghost"
                size="medium"
                // Says "file", rather than the generic `plus` this started as: next to a microphone
                // that also adds something to the question, a bare `+` gave no clue which of the
                // two took files.
                icon="file"
                iconOnly
                iconSize={ATTACH_ICON_SIZE}
                label={i18n('ask.attachments.add')}
                disabled={isStreaming || isTrayFull}
                onClick={handlePick}
                testID="ask-attach"
              />
            </AnchoredTooltip>
            {/* Between the two, because that is what it is: a file the user makes on the spot
                rather than one they already have, and a way to say something without typing it. */}
            <AskCameraButton
              onCapture={handlePhoto}
              disabled={isStreaming || isTrayFull}
              iconSize={CAMERA_ICON_SIZE}
            />
            {/* Both implementations are kept whole and neither knows the other exists, so switching
                is a one-line change in `voice/config.ts`. The branch is here rather than in a
                component of its own because dictation also occupies the bar above, and one
                component cannot render into two places. */}
            {ASK_VOICE_INPUT_MODE === 'record' ? (
              <AskRecordButton
                onRecorded={addRecording}
                disabled={isStreaming || isTrayFull}
                iconSize={MICROPHONE_ICON_SIZE}
              />
            ) : (
              <AskDictateButton
                dictation={dictation}
                disabled={isStreaming || isTrayFull}
                iconSize={MICROPHONE_ICON_SIZE}
              />
            )}
          </div>
        )}
        <div className={styles.inputWrapper}>
          <MBAutosizeTextInput
            ref={inputRef}
            style={{ flexGrow: 1 }}
            value={value}
            onChangeText={setValue}
            onSubmitEditing={handleSend}
            placeholder={placeholder}
            maxRows={5}
            autoFocus
            padding="none"
            ariaDescribedBy={hintId}
          />
          <span id={hintId} className={styles.srOnly}>
            {i18n('ask.charLimitHint', { limit: promptLimit })}
          </span>
        </div>
        {canSend && (
          <Button
            icon="send-filled"
            iconOnly
            size="large"
            variant="primary"
            // Kept visible rather than hidden when it cannot be pressed, so the disabled button
            // and the reason next to it explain each other: the red count, or a file still
            // uploading in the tray above.
            disabled={isStreaming || isOverLimit || isAnyUploading}
            onClick={handleSend}
            testID="ask-composer-send"
            className={bounceIn}
          />
        )}
        {showCount && (
          <span
            className={clsx(styles.counter, isOverLimit && styles.counterOver)}
            aria-hidden="true"
            data-testid="ask-composer-count"
          >
            {i18n('ask.charCount', { used: length, limit: promptLimit })}
          </span>
        )}
        <span className={styles.srOnly} aria-live="polite" data-testid="ask-composer-count-live">
          {announcement}
        </span>
      </div>
    </div>
  );
}
